import { UploadMediaResponse, UploadPdfResponse } from '../Models/Medias/Medias';
import { BaseDataService } from './Base/BaseDataService';
import { UploadData, UploadDataType } from '../../Libs/xhr/ApiClient';
import { XHRRequestCanceler } from '../../Libs/xhr/XHRRequestCanceler';

const MediaEndpoints = {
    UPLOAD_PICTURE: '/api/v1/back/images',
    UPLOAD_PICTURE_ON_NAS: '/api/v1/back/images?saveOnNas=true',
    UPLOAD_PDF: '/api/v1/back/pdfs',
    PICTURE: (pictureId: string) => `/api/v1/back/images/${pictureId}`,
};

export interface IMediaDataService {
    uploadPicture: (picture: File, saveOnNas?: boolean) => Promise<UploadMediaResponse>;
    getImageData: (
        imageId: string,
        params: { width: number; height: number }
    ) => Promise<ArrayBuffer>;

    getFileData: (
        fileUrl: string,
        cts: XHRRequestCanceler,
        progress: (value: number) => void
    ) => Promise<ArrayBuffer>;

    uploadPdf: (pdf: File) => Promise<UploadPdfResponse>;
}

class MediaDataService extends BaseDataService implements IMediaDataService {
    uploadPicture = async (picture: File, saveOnNas?: boolean): Promise<UploadMediaResponse> => {
        const dataToUpload: UploadData = {
            type: UploadDataType.File,
            value: picture,
        };
        return this.upload<UploadMediaResponse>(
            saveOnNas ? MediaEndpoints.UPLOAD_PICTURE_ON_NAS : MediaEndpoints.UPLOAD_PICTURE,
            dataToUpload
        );
    };

    //* get image bytes from url
    //* authorization  like : Authorization : Bearer TOKEN
    getImageData = async (
        imageId: string,
        params: { width: number; height: number }
    ): Promise<ArrayBuffer> => {
        const imageData: ArrayBuffer = await this.getRaw(
            MediaEndpoints.PICTURE(imageId),
            new XHRRequestCanceler(),
            {
                checkError: false,
                params,
                responseType: 'arraybuffer',
            }
        );
        return Promise.resolve(imageData);
    };

    getFileData = async (
        fileUrl: string,
        cts: XHRRequestCanceler,
        progress: (value: number) => void
    ): Promise<ArrayBuffer> => {
        const fileData: ArrayBuffer = await this.getRaw(
            fileUrl,
            cts,
            {
                checkError: false,
                responseType: 'arraybuffer',
            },
            progress
        );
        return Promise.resolve(fileData);
    };

    uploadPdf = async (pdf: File): Promise<UploadPdfResponse> => {
        const dataToUpload: UploadData = {
            type: UploadDataType.File,
            value: pdf,
        };
        return this.upload<UploadPdfResponse>(MediaEndpoints.UPLOAD_PDF, dataToUpload);
    };
}

const MediaDataServiceName = 'MediaDataService';

export { MediaDataService, MediaDataServiceName };
